import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Globe2 } from "lucide-react";
import { StockApi, type StockSearchResult } from "@/services/api";
import { cn } from "@/lib/utils";

export default function StockSearch({
  size = "lg",
  className,
}: {
  size?: "lg" | "sm";
  className?: string;
}) {
  const [kw, setKw] = useState("");
  const [results, setResults] = useState<StockSearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nav = useNavigate();
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (kw.trim().length < 1) {
        setResults([]);
        setError(null);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const r = await StockApi.search(kw);
        setResults(r);
        setOpen(true);
        if (r.length === 0) setError("未找到匹配的股票");
      } catch (e) {
        setError("搜索请求失败，请检查网络");
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [kw]);

  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const go = (code: string, mktNum: string) => {
    setOpen(false);
    setKw("");
    nav(`/stock/${code}?mkt=${mktNum}`);
  };

  return (
    <div ref={boxRef} className={cn("relative", className)}>
      <div
        className={cn(
          "flex items-center gap-3 border bg-ink-900/70 px-4 transition-colors",
          size === "lg" ? "py-3.5" : "py-2",
          open ? "border-gold" : "border-ink-600 hover:border-ink-500",
        )}
      >
        <Search size={size === "lg" ? 20 : 16} className="text-gold" />
        <input
          value={kw}
          onChange={(e) => setKw(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && results[0]) go(results[0].stockCode, results[0].mktNum);
          }}
          placeholder={size === "lg"
            ? "输入股票代码或名称（支持A股/港股/美股/ETF/指数）"
            : "搜索股票代码/名称"}
          className={cn(
            "flex-1 bg-transparent text-ink-100 placeholder:text-ink-500 outline-none",
            size === "lg" ? "text-base" : "text-sm",
          )}
        />
        {loading && (
          <span className="text-[0.625rem] font-mono text-gold animate-flicker">SEARCHING</span>
        )}
        <Globe2 size={12} className="text-ink-500" aria-label="全球股票实时数据" />
      </div>
      {open && (kw.trim().length > 0) && (
        <div className="absolute z-30 mt-1 w-full border border-ink-600 bg-ink-850 shadow-2xl shadow-black/60 max-h-96 overflow-auto">
          {error ? (
            <div className="px-4 py-6 text-center text-sm text-ink-400">{error}</div>
          ) : results.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-ink-400">未找到匹配的股票</div>
          ) : (
            results.map((s) => (
              <button
                key={`${s.mktNum}-${s.stockCode}`}
                onClick={() => go(s.stockCode, s.mktNum)}
                className="flex w-full items-center gap-4 px-4 py-2.5 text-left hover:bg-ink-800 transition-colors border-b border-ink-800 last:border-0"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-ink-100 truncate">{s.stockName}</div>
                  <div className="num text-[0.6875rem] text-ink-400">
                    {s.stockCode}
                    <span className="ml-2 text-ink-500">·</span>
                    <span className="ml-2 text-gold">{s.market}</span>
                  </div>
                </div>
                <span className="label-tag text-ink-400 border-ink-600 bg-ink-800 shrink-0">
                  实时行情
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
